import { Badge, type BadgeProps } from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

type BadgeConfig = {
	component: (badgeProps: BadgeProps) => JSX.Element;
	condition: (product: ProductNoEdibleInfo) => boolean;
};

const badges = ["exclusive", "recommended"] as const;
export type BadgeType = (typeof badges)[number];

function ExclusiveBadge(badgeProps: BadgeProps) {
	const { t } = useTranslation("product");

	return (
		<Badge
			key="exclusive"
			{...badgeProps}
			className="bg-gray-700 text-white uppercase"
		>
			{t("exclusive", { ns: "product" })}
		</Badge>
	);
}

function RecommendedBadge(badgeProps: BadgeProps) {
	const { t } = useTranslation("product");

	return (
		<Badge key="recommended" {...badgeProps} className="bg-blue-500 text-white">
			{t("recommended.title", { ns: "product" })}
		</Badge>
	);
}

function NewBadge(badgeProps: BadgeProps = {}) {
	const { t } = useTranslation("product");

	return (
		<Badge
			{...badgeProps}
			key="new"
			className="bg-ansay-background backdrop-blur-sm text-ansay-main rounded-full w-10 h-10 uppercase flex items-center justify-center"
		>
			{t("new")}
		</Badge>
	);
}

const badgesMap: Record<BadgeType, BadgeConfig> = {
	exclusive: {
		component: ExclusiveBadge,
		condition: (product) => product.exclusive && !product.featured,
	},
	recommended: {
		component: RecommendedBadge,
		condition: (product) => product.featured,
	},
};

const NEW_BADGE_THRESHOLD_DAYS = 14;

export function isCreatedWithinThreshold(
	createdAt?: Date | string | null,
): boolean {
	if (!createdAt) {
		return false;
	}

	const created = new Date(createdAt);
	const threshold = new Date();
	threshold.setDate(threshold.getDate() - NEW_BADGE_THRESHOLD_DAYS);

	return created > threshold;
}

export function getNewBadge(product: ProductNoEdibleInfo): JSX.Element | null {
	if (isCreatedWithinThreshold(product.createdAt)) {
		return <NewBadge />;
	}
	return null;
}
interface GetProductBadgesOptions {
	/** PDP exclusive section: always show Exklusiv even when product is also featured */
	forceExclusive?: boolean;
}

export function getProductBadges(
	product: ProductNoEdibleInfo,
	requestedBadges: BadgeType[],
	options?: GetProductBadgesOptions,
): JSX.Element[] {
	return Object.entries(badgesMap)
		.filter(([badgeName, config]) => {
			if (!requestedBadges.includes(badgeName as (typeof badges)[number])) {
				return false;
			}
			if (badgeName === "exclusive" && options?.forceExclusive) {
				return true;
			}
			return config.condition?.(product) ?? false;
		})
		.map(([_, config]) => config.component({ size: "xs" }));
}
